import React, { useMemo, useState } from 'react';
import api from '../services/api';
import useMt5Servers from '../hooks/useMt5Servers';

// Connect a new MT5 account, or re-enter credentials for an existing one.
//
// Connecting does NOT start a container — the account is saved and an admin
// verifies it. That is stated up front so nobody sits waiting for a terminal
// that isn't coming.

export default function ConnectAccountModal({ account, role, onClose, onSaved }) {
  const editing = !!account;
  const { servers, brokers } = useMt5Servers();

  const [form, setForm] = useState({
    label:            account?.label || '',
    accountNumber:    account?.accountNumber || '',
    server:           account?.server || '',
    mt5Password:      '',
    investorPassword: !!account?.investorPassword,
    // Per-account limits. Equity, balance and open positions are properties of
    // one broker account, so the limits measured against them are too — the
    // values on the Settings page are only the defaults for NEW accounts.
    maxDailyLoss:     account?.maxDailyLoss ?? 0,
    maxDrawdown:      account?.maxDrawdown ?? 0,
    maxOpenTrades:    account?.maxOpenTrades ?? 20,
    allowFollowers:   account?.allowFollowers !== false,
    minCopierEquity:  account?.minCopierEquity ?? 0,
    // PROFIT-SHARE BILLING — TEMPORARILY DISABLED (2026-08-19), see worker.js.
    // This one line is deliberately LEFT LIVE: nothing reads it while the input
    // and the payload line below are commented out, and keeping it means a
    // restore is two uncomments rather than three.
    profitSharePct:   account?.profitSharePct ?? 0,
  });
  // Pre-select the broker whose list contains the current server, so editing
  // an existing account opens with its own broker already chosen.
  const [broker, setBroker] = useState(() => {
    const match = mt5ServerBroker(account?.server, []);
    return match || '';
  });
  const [manual, setManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const brokerForCurrent = useMemo(
    () => mt5ServerBroker(form.server, servers),
    [form.server, servers],
  );
  const effectiveBroker = broker || brokerForCurrent || '';
  const serversForBroker = useMemo(
    () => servers.filter(s => s.broker === effectiveBroker),
    [servers, effectiveBroker],
  );

  function change(e) {
    const { name, type, value, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!form.accountNumber.trim()) { setError('MT5 account number is required.'); return; }
    if (!form.server.trim()) { setError('MT5 server is required.'); return; }
    if (!editing && !form.mt5Password) { setError('MT5 password is required.'); return; }

    setLoading(true);
    try {
      const payload = {
        label: form.label.trim(),
        accountNumber: form.accountNumber.trim(),
        server: form.server.trim(),
        investorPassword: form.investorPassword,
      };
      if (editing) {
        payload.maxDailyLoss   = Number(form.maxDailyLoss) || 0;
        payload.maxDrawdown    = Number(form.maxDrawdown) || 0;
        payload.maxOpenTrades  = parseInt(form.maxOpenTrades, 10) || 0;
        if (role === 'trader') {
          payload.allowFollowers  = !!form.allowFollowers;
          payload.minCopierEquity = Number(form.minCopierEquity) || 0;
          // PROFIT-SHARE BILLING — TEMPORARILY DISABLED (2026-08-19). The backend
          // no longer accepts this field, so sending it would 400 the whole save.
          // payload.profitSharePct  = Number(form.profitSharePct) || 0;
        }
      }
      // On edit an empty password means "leave it alone" — sending '' would
      // wipe a working credential.
      if (form.mt5Password) payload.mt5Password = form.mt5Password;

      const res = editing
        ? await api.put(`/accounts/${account.id}`, payload)
        : await api.post('/accounts', payload);
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the MT5 account.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-backdrop-custom" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="card mb-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              {editing ? 'Edit MT5 account' : 'Connect an MT5 account'}
            </h5>
            <button className="close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}

            {!editing && (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                Your credentials are stored encrypted. An admin verifies the account
                before its MT5 terminal is started — you'll be notified when it's live.
              </div>
            )}
            {editing && (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                Changing the login, server or password restarts this account's MT5
                terminal so the new details take effect. Leave the password blank to
                keep the current one.
              </div>
            )}

            <form onSubmit={submit}>
              <div className="form-group">
                <label className="text-white-50">Name (optional)</label>
                <input
                  className="form-control"
                  name="label"
                  maxLength={40}
                  placeholder="e.g. Deriv Demo, Exness Live"
                  value={form.label}
                  onChange={change}
                />
                <small className="text-white-50">
                  Only you see this. It labels the account in your switcher.
                </small>
              </div>

              <div className="form-group">
                <label className="text-white-50">MT5 account number</label>
                <input
                  className="form-control"
                  name="accountNumber"
                  inputMode="numeric"
                  placeholder="e.g. 12345678"
                  value={form.accountNumber}
                  onChange={change}
                />
              </div>

              {!manual ? (
                <>
                  <div className="form-group">
                    <label className="text-white-50">Broker</label>
                    <select
                      className="form-control"
                      value={effectiveBroker}
                      onChange={(e) => {
                        setBroker(e.target.value);
                        setForm(prev => ({ ...prev, server: '' }));
                      }}
                    >
                      <option value="">Select your broker…</option>
                      {brokers.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="text-white-50">Server</label>
                    <select
                      className="form-control"
                      name="server"
                      value={form.server}
                      onChange={change}
                      disabled={!effectiveBroker}
                    >
                      <option value="">
                        {effectiveBroker ? 'Select a server…' : 'Pick a broker first'}
                      </option>
                      {serversForBroker.map(s => (
                        <option key={s.server} value={s.server}>
                          {s.server}{s.type ? ` (${s.type})` : ''}
                        </option>
                      ))}
                    </select>
                    <small className="text-white-50">
                      Can't find yours?{' '}
                      <button type="button" className="btn btn-link p-0" onClick={() => setManual(true)}>
                        Type it manually
                      </button>
                    </small>
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label className="text-white-50">Server</label>
                  <input
                    className="form-control"
                    name="server"
                    placeholder="e.g. Deriv-Demo"
                    value={form.server}
                    onChange={change}
                  />
                  <small className="text-white-50">
                    Must match the server string in your MT5 terminal exactly.{' '}
                    <button type="button" className="btn btn-link p-0" onClick={() => setManual(false)}>
                      Pick from the list instead
                    </button>
                  </small>
                </div>
              )}

              <div className="form-group">
                <label className="text-white-50">
                  MT5 password{editing ? ' (leave blank to keep current)' : ''}
                </label>
                <input
                  type="password"
                  className="form-control"
                  name="mt5Password"
                  autoComplete="new-password"
                  value={form.mt5Password}
                  onChange={change}
                />
              </div>

              {role === 'trader' && (
                <div className="form-check mb-3">
                  <label className="form-check-label">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="investorPassword"
                      checked={form.investorPassword}
                      onChange={change}
                    />
                    <span className="form-check-sign" />
                    This is the investor (read-only) password
                  </label>
                  <small className="text-white-50 d-block">
                    Read-only credentials can broadcast your trades but never place any.
                    Copiers must use a master password.
                  </small>
                </div>
              )}

              {editing && (
                <>
                  <hr style={{ borderColor: 'rgba(255,255,255,.08)' }} />
                  <h6 className="text-white-50" style={{ fontSize: 12, letterSpacing: '.06em' }}>
                    LIMITS FOR THIS ACCOUNT
                  </h6>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="text-white-50">Max daily loss (%)</label>
                        <input type="number" min="0" max="100" step="0.1" className="form-control"
                          name="maxDailyLoss" value={form.maxDailyLoss} onChange={change} />
                        <small className="text-white-50">0 disables.</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="text-white-50">Max drawdown (%)</label>
                        <input type="number" min="0" max="100" step="0.1" className="form-control"
                          name="maxDrawdown" value={form.maxDrawdown} onChange={change} />
                        <small className="text-white-50">0 disables.</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="text-white-50">Max open trades</label>
                        <input type="number" min="0" max="500" step="1" className="form-control"
                          name="maxOpenTrades" value={form.maxOpenTrades} onChange={change} />
                      </div>
                    </div>
                  </div>

                  {role === 'trader' && (
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check mb-3">
                          <label className="form-check-label">
                            <input type="checkbox" className="form-check-input"
                              name="allowFollowers" checked={form.allowFollowers} onChange={change} />
                            <span className="form-check-sign" />
                            Allow copiers to follow this account
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="text-white-50">Minimum copier equity</label>
                          <input type="number" min="0" step="1" className="form-control"
                            name="minCopierEquity" value={form.minCopierEquity} onChange={change} />
                          <small className="text-white-50">0 = no minimum.</small>
                        </div>
                      </div>
                      {/* PROFIT-SHARE BILLING — TEMPORARILY DISABLED (2026-08-19); see worker.js */}
                      {/*
                      <div className="col-12">
                        <div className="form-group">
                          <label className="text-white-50">Profit share (%)</label>
                          <input type="number" min="0" max="100" step="1" className="form-control"
                            name="profitSharePct" value={form.profitSharePct} onChange={change} />
                          <small className="text-white-50">
                            Your share of the profit you make each copier, charged daily on closed
                            positions. <strong>0 = free to follow.</strong> The platform adds its own
                            share on top; copiers see the full split before they follow. Changing
                            this only affects NEW followers &mdash; existing ones keep the terms they
                            agreed to.
                          </small>
                        </div>
                      </div>
                      */}
                    </div>
                  )}
                </>
              )}

              <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-neutral mr-2" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving…' : (editing ? 'Save changes' : 'Connect account')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// Which broker does this server string belong to, per the picker's list?
function mt5ServerBroker(server, servers) {
  if (!server) return '';
  const hit = (servers || []).find(s => s.server === server);
  return hit ? hit.broker : '';
}
